import styles from './styles.module.scss';

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@reach/router';
import clsx from 'clsx';

import { ModalForm } from 'components/ModalForm';
import { Button } from '@clover/web-lib-consumer-ui';

import { MobileGiftCardForm } from '../Form/Mobile';
import { GiftCardFormValues } from '../Form/model';
import { Money } from 'components/Money';
import { RouteFC } from 'util/routeTypes';
import { useGiftCardCart, useGiftCardCartDispatch } from 'hooks/context/giftCardCart';
import { GIFT_AMOUNT_MIN } from '../const';
import { FormSubmitHandler } from 'components/Form';
import { useMerchant } from 'hooks/context/merchant';
import { MENU_ITEM_MODAL_TEST_ID } from 'routes/MenuItemModal/MenuItemModalTestIds';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { MerchantTitle } from 'components/MerchantTitle';

type GiftCardModalProps = {
  pageTitleKey: string;
  backTo: string;
  initialValue?: GiftCardFormValues;
  onSubmit: (value: GiftCardFormValues) => void;
};

const GiftCardModal: React.FC<GiftCardModalProps> = ({ pageTitleKey, initialValue, onSubmit, backTo })=> {
  const { isLoading } = useMerchant();
  const [value, setValue] = useState<GiftCardFormValues>(initialValue || { amount: GIFT_AMOUNT_MIN, isCustomer: false });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onClose = useCallback(() => navigate(backTo), [backTo, navigate]);

  const handleSubmit: FormSubmitHandler = useCallback((e, isValid) => {
    e.preventDefault();

    if (!isValid) return;

    onSubmit(value);
    onClose();
  }, [onClose, onSubmit, value]);

  return (
    <ModalForm
      isOpen
      onSubmit={handleSubmit}
      onClose={onClose}
      title={t('giftCards.digitalGiftCard')}
      testId={MENU_ITEM_MODAL_TEST_ID.root}
    >
      <MerchantTitle i18nKey={pageTitleKey} />
      <MobileGiftCardForm hideTitle hideButton className={styles.form} value={value} onChange={setValue} />
      <footer>
        <Button
           disabled={isLoading}
           className={clsx(styles.button)}
           type="submit"
           data-testid={MENU_ITEM_MODAL_TEST_ID.submitButton}
        >
          {initialValue ? t('updateOrder') : t('addToOrder')}
          <Money amount={value.amount} />
        </Button>
      </footer>
    </ModalForm>
  );
};

export const AddGiftCardModal: RouteFC<Record<string, unknown>, {backTo?: string}> = ({ backTo = './' }) => {
  const { add } = useGiftCardCartDispatch();

  return <GiftCardModal backTo={backTo} onSubmit={({ amount, note, ...recipient })=> add({ amount, recipient, note })} pageTitleKey="helmet.titleGiftCard" />;
};

export const EditGiftCardModal: RouteFC<{ index?: number}, { backTo?: string }> = ({ index, backTo = '../' }) => {
  const navigate = useSmartNavigate();
  const { items, update } = useGiftCardCart();

  if (index === undefined || !items[index]) {
    navigate(backTo);

    return null;
  }

  const { amount, recipient, note } = items[index];

  return <GiftCardModal backTo={backTo} initialValue={{ amount, note, ...recipient }} onSubmit={({ amount, note, ...recipient })=> update(index, { amount, recipient, note })} pageTitleKey="helmet.titleEditGiftCard" />;
};

export const SingleGiftCardModal: RouteFC = () => {
  const { items } = useGiftCardCart();

  if (items.length > 0) {
    return <EditGiftCardModal index={0} backTo="./" />;
  }

  return <AddGiftCardModal />;
};

